import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SyntheticEvent } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  ChevronDownIcon,
  CircleAlertIcon,
  Trash2Icon,
  UploadIcon,
} from "@lucide/icons";
import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { useTheme } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  type AccordionTitleProps,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import {
  Card,
  CardContent,
  CardGroup,
  CardHeader,
  CardMeta,
} from "simplydo/react/ui/card";
import { Form } from "simplydo/react/ui/form";
import { H3, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Tab, TabPane } from "simplydo/react/ui/tab";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import ideasImage from "src/images/ideas.png";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { EmptyBox } from "components/lib/UI";

import AiWarning from "../AiWarning";
import { useTrayStatus } from "../Notifications";
import type { AiAttachment, AiChallengeFormatTaskPayload } from "./types";

const aiAttachmentTypes = {
  pdf: "application/pdf",
  csv: "text/csv",
  txt: "text/plain",
  rtf: "text/rtf",
  md: "text/md",
  html: "text/html",
  xml: "text/xml",
};

type IIdeaTemplateChooser = {
  onTemplateSelected: (template: any) => void;
  challengeId: string;
};

type IChallengeSearchState = { page: number; limit: number; query: string };

const IdeaTemplateChooser = ({
  onTemplateSelected,
  challengeId,
}: IIdeaTemplateChooser) => {
  const [_loading, setLoading] = useState<boolean>(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiAttachments, setAiAttachments] = useState<AiAttachment[]>([]);
  const [aiIsLoading, setAiIsLoading] = useState(false);
  const aiAttachmentInputRef = useRef<HTMLInputElement>(null);
  const [challenges, setChallenges] = useState<OpenAPI.Schemas["Challenge"][]>(
    [],
  );
  const [nextPageAvailable, setNextPageAvailable] = useState<boolean>(false);
  const [prevPageAvailable, setPrevPageAvailable] = useState<boolean>(false);
  const [accordionIndex, setAccordionIndex] = useState<number>(-1);
  const [selectedChallenge, setSelectedChallenge] = useState<string>("");
  const [challengeSearchState, setChallengeSearchState] =
    useState<IChallengeSearchState>({ page: 1, limit: 9, query: "" });
  const [_, setTrayStatus] = useTrayStatus();
  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId!], {
      isEnabled: !!challengeId,
    }),
  );
  const challenge = challengeQuery.data;

  const user = useAppSelector((state) => state.user);
  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const { t } = useTranslation();
  const theme = useTheme();

  const templates = [
    {
      _id: "3is",
      name: 'The "3 Is"',
      description: `A short template designed to quickly collect ${t("generic.ideas")} for internal innovation. Includes a self-assessment rating against Importance, Impact, and ease of Implementation.`,
      image: util.getCdnFile("images/format_3is.png"),
      body: [
        {
          name: `The ${t("generic.idea")}`,
          type: "custom",
          fields: [
            {
              wordLimit: "50",
              id: "overview",
              title: `What is your ${t("generic.idea")} about?`,
              description: `Briefly describe your ${t("generic.idea")} as you see it.`,
              type: "longtext",
            },
            {
              id: "importance",
              title: `How important is this ${t("generic.idea")} to you?`,
              description: "10 = Very Important",
              type: "slider",
            },
            {
              id: "implementation",
              title: "How easy would it be to implement?",
              description: "10 = Very easy",
              type: "slider",
            },
            {
              id: "impact",
              title: "How much impact could it have?",
              description: "10 = Game changing",
              type: "slider",
            },
            {
              id: "image",
              title: `Please upload an image of your ${t("generic.idea")} `,
              description: "Optional ",
              type: "image",
            },
          ],
        },
      ],
    },
    {
      _id: "scratch",
      name: "Start from scratch",
      description:
        "None of these suit? No problem - you can craft your own format from scratch using our format builder.",
      image: util.getCdnFile("images/format_scratch.png"),
      body: [{ name: `The ${t("generic.idea")}`, type: "custom", fields: [] }],
    },
  ];

  const userId = user?._id;
  const getChallenges = useThrottle(
    () => {
      if (!userId) {
        return;
      }
      setLoading(true);
      setSelectedChallenge("");
      api.users.getChallenges(
        userId,
        challengeSearchState,
        (data) => {
          setChallenges(data.challenges);
          setNextPageAvailable(data.nextPageAvailable);
          setPrevPageAvailable(data.prevPageAvailable);
          setLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setLoading(false);
        },
      );
    },
    500,
    [userId, challengeSearchState],
  );

  useEffect(() => {
    getChallenges();
  }, [getChallenges]);

  const onClickDefaultTemplate = useCallback(
    (template: { _id: string }) => {
      onTemplateSelected(template);
      setSelectedChallenge(template._id);
    },
    [onTemplateSelected],
  );

  const onClickChallenge = useCallback(
    (clickedChallenge: OpenAPI.Schemas["Challenge"]) => {
      setSelectedChallenge(clickedChallenge._id);
      api.challenges.getTemplate(
        clickedChallenge._id,
        (newTemplate) => {
          if (newTemplate.body) {
            onTemplateSelected(newTemplate);
          } else {
            toast.error("Challenge has no valid template.");
          }
        },
        () => {},
      );
    },
    [onTemplateSelected],
  );

  const handleAccordionClick = useCallback(
    (_e: SyntheticEvent, titleProps: AccordionTitleProps) => {
      const { index } = titleProps;
      if (index === undefined) return;
      const indexValue = typeof index === "number" ? index : Number(index);
      const newIndex = accordionIndex === indexValue ? -1 : indexValue;
      setAccordionIndex(newIndex);
    },
    [accordionIndex],
  );

  const generateChallengeFormat = () => {
    if (!challengeId || !user?._id) return;
    setAiIsLoading(true);
    const taskPayload: AiChallengeFormatTaskPayload = {
      forType: "challenge",
      forId: challengeId,
      type: "aiChallengeFormat",
      context: { prompt: aiInput, attachments: aiAttachments },
    };
    api.users.createLongRunningTask(
      user._id,
      taskPayload,
      () => {
        setTrayStatus({ open: true, activeIndex: 0 });
        api.invalidateQueries(api.notifications.get, []);
        api.invalidateQueries(api.challenges.get, [challengeId]);
        toast.success(
          `Translation task created. This may take a while to complete, we will notify you when it's done.`,
        );
        setAiModalOpen(false);
        setAiIsLoading(false);
      },
      () => {
        setAiIsLoading(false);
        toast.error(
          `Translation failed. Please try again later or contact support if the issue persists.`,
        );
      },
    );
  };

  return (
    <div>
      <H3>
        {!selectedChallenge ? (
          <Icon icon={CircleAlertIcon} color={"red"} />
        ) : null}
        {t("challenge.templateChooser.title")}
      </H3>
      <Modal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        size="small"
      >
        <ModalHeader>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>Automatically generate a format</span>
            <Label color="purple" size="small" style={{ marginLeft: 10 }}>
              ✨ AI-powered
            </Label>
          </div>
        </ModalHeader>
        <ModalContent>
          <Tab
            panes={[
              {
                menuItem: "Describe your format",
                render: () => (
                  <TabPane>
                    <Form>
                      <div style={{ marginBottom: 16 }}>
                        {util.appName()} AI will generate a format for you based
                        on the description and instructions you provide below.
                        The more precise you are, the more accurate your format
                        will be.
                      </div>
                      <TextArea
                        placeholder="Describe your format here..."
                        value={aiInput}
                        onChange={(_e, { value }) => {
                          setAiInput(value as string);
                        }}
                      />
                    </Form>
                  </TabPane>
                ),
              },
              {
                menuItem: "or upload a file",
                render: () => (
                  <TabPane>
                    <div style={{ marginBottom: 16 }}>
                      Choose a PDF or CSV file containing the details about the
                      format you want to generate.
                    </div>
                    <input
                      accept={Object.values(aiAttachmentTypes).join(",")}
                      style={{ display: "none" }}
                      ref={aiAttachmentInputRef}
                      type="file"
                      multiple
                      onChange={async (e) => {
                        const files: AiAttachment[] = [];
                        const fileList = e.target.files;
                        if (!fileList?.length) {
                          return;
                        }

                        const toBase64 = async (file: File) =>
                          await new Promise<string>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.readAsDataURL(file);
                            reader.onload = () => {
                              const dataUrl = reader.result as string;
                              const base64 = dataUrl.split(",")[1] ?? "";
                              resolve(base64);
                            };
                            reader.onerror = reject;
                          });

                        for (let i = 0; i < fileList.length; i++) {
                          const fileData = fileList[i];
                          if (!fileData) continue;
                          const fileName = fileData.name;
                          try {
                            const base64 = await toBase64(fileData);
                            files.push({
                              name: fileName,
                              type: fileData.type,
                              size: fileData.size,
                              data: base64,
                            });
                          } catch (_e) {
                            toast.error("Error uploading file");
                          }
                        }

                        setAiAttachments((prev) => [...prev, ...files]);
                        setAiInput(
                          "Use the attached files to generate a format",
                        );
                      }}
                    />
                    <Button
                      onClick={() => {
                        if (aiAttachmentInputRef.current) {
                          aiAttachmentInputRef.current.click();
                        }
                      }}
                    >
                      <Icon icon={UploadIcon} />
                      Upload attachments
                    </Button>
                    {aiAttachments.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHeaderCell>File name</TableHeaderCell>
                            <TableHeaderCell>Type</TableHeaderCell>
                            <TableHeaderCell>Actions</TableHeaderCell>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {aiAttachments.map((attachment, i) => (
                            <TableRow key={i}>
                              <TableCell>{attachment.name}</TableCell>
                              <TableCell>{attachment.type}</TableCell>
                              <TableCell>
                                <Button
                                  onClick={() => {
                                    setAiAttachments((prev) =>
                                      prev.filter((_, index) => index !== i),
                                    );
                                    if (aiAttachments.length === 1) {
                                      setAiInput("");
                                    }
                                  }}
                                  variant="outline"
                                >
                                  <Icon icon={Trash2Icon} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabPane>
                ),
              },
            ]}
          />
          <AiWarning />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setAiModalOpen(false)}>Cancel</Button>
          <Button
            disabled={!aiInput}
            onClick={() => generateChallengeFormat()}
            loading={aiIsLoading}
            color="primary"
          >
            Generate
          </Button>
        </ModalActions>
      </Modal>
      <CardGroup itemsPerRow={3} stackable>
        {templates.map((template, i) => {
          // Temporarily disable challenge capture for regular users/admins
          if (
            template._id === "challengeCapture" &&
            !util.hasPermission(user, "super.viewDashboard")
          )
            return null;
          return (
            <Card key={i}>
              {template.image && (
                <div
                  style={{
                    height: 150,
                    backgroundPosition: "center center",
                    backgroundSize: "cover",
                    backgroundImage: `url(${template.image})`,
                  }}
                />
              )}
              <CardContent>
                <CardHeader>{template.name}</CardHeader>
                <CardMeta>{template.description}</CardMeta>
              </CardContent>
              <CardContent extra>
                <Button
                  fluid
                  disabled={
                    !canManageChallenge &&
                    !util.hasPermission(
                      user,
                      "challenge.editIdeaFormat",
                      challenge?._id,
                    )
                  }
                  title={template.name}
                  onClick={() => onClickDefaultTemplate(template)}
                  color="primary"
                >
                  Use this format
                </Button>
              </CardContent>
            </Card>
          );
        })}
        {(util.organisationFeaturesEnabled(user, ["aiGenChallengeFormat"]) ||
          util.hasPermission(user, "super.viewDashboard")) && (
          <Card>
            <div
              style={{
                height: 150,
                backgroundPosition: "center center",
                backgroundSize: "cover",
                backgroundImage: `url(${ideasImage})`,
              }}
            />
            <CardContent>
              <CardHeader>Automatically Generated </CardHeader>
              <CardMeta>
                Describe your format with text or files, and {util.appName()}{" "}
                will automatically generate a format for you.
              </CardMeta>
            </CardContent>
            <CardContent extra>
              <Button
                fluid
                disabled={
                  !canManageChallenge &&
                  !util.hasPermission(
                    user,
                    "challenge.editIdeaFormat",
                    challenge?._id,
                  )
                }
                onClick={() => setAiModalOpen(true)}
                color="primary"
              >
                Get started
              </Button>
            </CardContent>
          </Card>
        )}
      </CardGroup>

      {challenges.length > 0 ||
      (!!challengeSearchState.query &&
        (canManageChallenge ||
          util.hasPermission(
            user,
            "challenge.editIdeaFormat",
            challenge?._id,
          ))) ? (
        <Accordion style={{ marginTop: 20 }}>
          <AccordionTitle
            active={accordionIndex === 0}
            index={0}
            onClick={handleAccordionClick}
          >
            <H4>
              Re-use a format from one of your existing{" "}
              {t("generic.challenges")}
            </H4>
          </AccordionTitle>
          <AccordionContent active={accordionIndex === 0} index={0}>
            <Input
              placeholder={`Search for ${t("generic.challengeWithArticle")}..`}
              onChange={(_e, { value }) =>
                setChallengeSearchState((prevState) => ({
                  ...prevState,
                  page: 1,
                  query: value,
                }))
              }
              style={{ marginBottom: 10 }}
              value={challengeSearchState.query}
            />
            {challenges?.length ? (
              <CardGroup itemsPerRow={theme.sizes.isComputer ? 3 : 2}>
                {challenges
                  .filter((c) => c._id !== challengeId)
                  .map((c) => (
                    <Card key={c._id}>
                      <div
                        style={{
                          height: 150,
                          backgroundPosition: "center center",
                          backgroundSize: "cover",
                          backgroundImage: `url(${util.mixinCssUrlFallback(util.challengeImage(c), util.challengeImage())})`,
                        }}
                      />
                      <CardContent>
                        <CardHeader>{c.name}</CardHeader>
                        <CardMeta>
                          {t("challenge.templateChooser.copyFromChallenge")}
                        </CardMeta>
                        <CardMeta style={{ marginTop: 10 }}>
                          <Link
                            className="with-border"
                            to={`/challenges/${c._id}/format`}
                            target="_blank"
                          >
                            View format
                          </Link>
                        </CardMeta>
                      </CardContent>
                      <CardContent extra>
                        {/* @ts-ignore */}
                        <UserChip user={{ ...user, emails: null }} />
                      </CardContent>
                      <CardContent extra>
                        <Button
                          fluid
                          onClick={() => onClickChallenge(c)}
                          color="primary"
                        >
                          Use this format
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
              </CardGroup>
            ) : (
              <EmptyBox title={undefined} message={undefined} style={undefined}>
                No {t("generic.challenges")} found
              </EmptyBox>
            )}
            {nextPageAvailable || prevPageAvailable ? (
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 10,
                }}
              >
                {prevPageAvailable ? (
                  <Button
                    size="small"
                    onClick={() =>
                      setChallengeSearchState((prevState) => ({
                        ...prevState,
                        page: prevState.page - 1,
                      }))
                    }
                  >
                    Prev page
                  </Button>
                ) : (
                  <div />
                )}
                {nextPageAvailable ? (
                  <Button
                    size="small"
                    onClick={() =>
                      setChallengeSearchState((prevState) => ({
                        ...prevState,
                        page: prevState.page + 1,
                      }))
                    }
                  >
                    Next page
                  </Button>
                ) : null}
              </div>
            ) : null}
          </AccordionContent>
        </Accordion>
      ) : null}
    </div>
  );
};

export default IdeaTemplateChooser;
